/**
 * Ask API Service
 * Calls the /api/v1/ask endpoint for LLM-based queries
 * Supports async polling for long-running operations
 */

import { authenticatedFetch, ApiError, type ApiResponse } from './apiService'
import { API_ENDPOINTS, getAskOperationUrl } from './apiEndpoints'
import type { AskRequest, AskPostResponse, AskOperationResponse } from './askApiTypes'
import { isAskAccepted, isAskCompleted } from './askApiTypes'

const POLL_SCHEDULE_MS = [150, 250, 400, 650, 1000, 1500, 1500, 1500, 2500, 2500, 3000, 3000]
const STEADY_STATE_POLL_MS = 3000
const TOTAL_TIMEOUT_MS = 5 * 60 * 1000

/**
 * Abort-aware delay
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)

    function onAbort() {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }

    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Attempts to extract an .error message from a JSON error body string
 */
function extractErrorMessage(rawError: string): string {
  try {
    const parsed = JSON.parse(rawError) as { error?: string }
    if (typeof parsed.error === 'string') {
      return parsed.error
    }
  } catch {
    // Not JSON, return as-is
  }
  return rawError
}

/**
 * POST to /api/v1/ask
 */
export async function askApi(
  request: AskRequest,
  options?: { signal?: AbortSignal }
): Promise<ApiResponse<AskPostResponse>> {
  return authenticatedFetch<AskPostResponse>(API_ENDPOINTS.ask, {
    method: 'POST',
    body: JSON.stringify(request),
    signal: options?.signal,
  })
}

/**
 * GET a single poll of an ask operation
 */
export async function getAskOperation(
  operationId: string,
  options?: { signal?: AbortSignal }
): Promise<ApiResponse<AskOperationResponse>> {
  return authenticatedFetch<AskOperationResponse>(getAskOperationUrl(operationId), {
    method: 'GET',
    signal: options?.signal,
  })
}

/**
 * High-level async ask: submits, polls until complete, and parses JSON output.
 * Throws on all errors. Returns parsed T on success.
 */
export async function askApiAsyncJson<T>(
  request: AskRequest,
  options?: { signal?: AbortSignal }
): Promise<T> {
  const signal = options?.signal

  // 1. Submit the request
  const postResponse = await askApi(request, { signal })

  if (postResponse.error) {
    throw new ApiError(extractErrorMessage(postResponse.error), postResponse.status)
  }
  if (!postResponse.data) {
    throw new Error('Ask API returned no data')
  }

  // 2. If already completed, parse and return
  if (isAskCompleted(postResponse.data)) {
    return parseOutput<T>(postResponse.data.output)
  }

  // 3. Must be accepted — extract operationId and poll
  if (!isAskAccepted(postResponse.data)) {
    throw new Error('Unexpected Ask API response status')
  }

  const { operationId } = postResponse.data
  if (!operationId) {
    throw new Error('Ask API accepted response missing operationId')
  }

  const startTime = Date.now()
  let pollIndex = 0

  while (true) {
    // Check timeout
    if (Date.now() - startTime >= TOTAL_TIMEOUT_MS) {
      throw new Error('Ask API polling timed out after 5 minutes')
    }

    // Wait per schedule
    const waitMs =
      pollIndex < POLL_SCHEDULE_MS.length
        ? POLL_SCHEDULE_MS[pollIndex]
        : STEADY_STATE_POLL_MS
    await delay(waitMs, signal)

    // Poll
    const pollResponse = await getAskOperation(operationId, { signal })

    if (pollResponse.error) {
        throw new ApiError(extractErrorMessage(pollResponse.error), pollResponse.status)
    }
    if (!pollResponse.data) {
      throw new Error('Ask API poll returned no data')
    }

    if (isAskCompleted(pollResponse.data)) {
      return parseOutput<T>(pollResponse.data.output)
    }

    if (!isAskAccepted(pollResponse.data)) {
      throw new Error('Unexpected Ask API poll response status')
    }

    pollIndex++
  }
}

/**
 * Parses the output JSON string from a completed response
 */
function parseOutput<T>(output: string | undefined | null): T {
  if (output == null || output === '') {
    throw new Error('Ask API completed response missing output')
  }

  try {
    return JSON.parse(output) as T
  } catch {
    throw new Error('Failed to parse Ask API output as JSON')
  }
}
